import React, { useState, useEffect } from 'react'
import { Link } from 'gatsby'
import {
  useTranslation,
  useI18next,
  Link as I18NLink,
} from 'gatsby-plugin-react-i18next'

import cx from '../../utils/cx'
import useIntersectionObserver from './useIntersectionObserver'
import routes from './routes'
import Logo from '../../img/logo.inline.svg'
import HeaderLink from './HeaderLink'
import LogoContainer from './LogoContainer'

function LanguageSelect({ className }) {
  const { languages, originalPath } = useI18next()

  return (
    <ul className={cx('flex', className)}>
      {languages.map((lng, i) => {
        return (
          <>
            {i ? <span>&nbsp;|&nbsp;</span> : null}
            <li key={lng}>
              <I18NLink to={originalPath} language={lng}>
                {lng}
              </I18NLink>
            </li>
          </>
        )
      })}
    </ul>
  )
}

export default function Header() {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [activeId, setActiveId] = useState()
  useIntersectionObserver(setActiveId)

  const menuTriggerClick = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    const onScroll = () => {
      if (isOpen) {
        setIsOpen(false)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [isOpen])

  return (
    <header
      id="header"
      className="absolute z-50 w-full bg-white shadow md:fixed"
    >
      <div className="w-full px-4 sm:px-12">
        <div className="w-full items-center justify-between md:flex">
          <div
            id="primary-menu-trigger"
            className="relative flex items-center justify-between py-4"
          >
            <MobileMenuButton onClick={menuTriggerClick} />
            <div
              id="logo"
              className="absolute left-1/2 -translate-x-1/2 sm:static sm:translate-x-0"
            >
              <Link to="/" title="Home">
                <LogoContainer>
                  <Logo className="fill-gray-22 h-full w-full" />
                </LogoContainer>
                <Logo className="w-28 fill-gray-400 md:hidden" />
              </Link>
            </div>
            <div className="md:hidden">
              <HeaderLink
                text={t(routes.contact.text)}
                id={routes.contact.id}
                isContact
                linkClassNames="!p-1.5 text-[0.65rem] tracking-tight uppercase hover:bg-gray-400"
              />
            </div>
          </div>

          {/*-- Primary Navigation
        ============================================= --*/}
          <nav id="primary-menu">
            {/* Desktop menu */}
            <ul className="hidden items-center space-x-8 font-primary text-xs font-bold uppercase tracking-wide text-gray-400 md:flex md:text-[0.85rem]">
              {Object.values(routes).map((route) => (
                <li key={`nav-${route.text}`} className="group">
                  <HeaderLink
                    text={t(route.text)}
                    id={route.id}
                    isActive={route.id == activeId}
                    isContact={route.id == 'contact'}
                  />
                </li>
              ))}
              <LanguageSelect />
            </ul>

            {/* Mobile menu */}
            <div
              className={cx(
                'absolute left-0 top-auto z-10 w-full bg-white px-8 md:hidden',
                isOpen ? 'block' : 'hidden'
              )}
            >
              <ul className="divide-y divide-gray-100 divide-opacity-80 font-primary text-xs font-bold uppercase tracking-widest text-gray-400">
                {Object.values(routes).map((route) => (
                  <li key={`nav-${route.text}`} className="px-1.5 py-4">
                    <HeaderLink
                      text={t(route.text)}
                      id={route.id}
                      setIsOpen={() => setIsOpen(false)}
                    />
                  </li>
                ))}
                <LanguageSelect className="px-1.5 py-4" />
              </ul>
            </div>
          </nav>
          {/* #primary-menu end */}
        </div>
      </div>
    </header>
  )
}

function MobileMenuButton({ onClick }) {
  return (
    <button
      type="button"
      aria-label="navigation menu icon"
      onClick={onClick}
      className="focus:outline-none md:hidden"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        id="Layer_1"
        version="1.1"
        viewBox="0 0 32 32"
        xmlSpace="preserve"
        className="h-6 fill-current text-gray-400"
      >
        <path d="M4,10h24c1.104,0,2-0.896,2-2s-0.896-2-2-2H4C2.896,6,2,6.896,2,8S2.896,10,4,10z M28,14H4c-1.104,0-2,0.896-2,2  s0.896,2,2,2h24c1.104,0,2-0.896,2-2S29.104,14,28,14z M28,22H4c-1.104,0-2,0.896-2,2s0.896,2,2,2h24c1.104,0,2-0.896,2-2  S29.104,22,28,22z" />
      </svg>
    </button>
  )
}
